import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { getCoach, requestProfileUpdate, updateEngagements } from "@/lib/coaches.functions";
import { ArrowLeft, Copy, RefreshCcw } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/coacher/$id")({
  loader: ({ params }) => getCoach({ data: { id: params.id } }),
  component: CoachDetail,
  errorComponent: ({ error }) => (
    <AppShell><div className="p-8 text-sm text-destructive">{error.message}</div></AppShell>
  ),
  notFoundComponent: () => <AppShell><div className="p-8">Coachen hittades inte.</div></AppShell>,
});

function CoachDetail() {
  const { coach } = Route.useLoaderData();
  const router = useRouter();
  const requestUpdate = useServerFn(requestProfileUpdate);
  const updateEng = useServerFn(updateEngagements);
  const [link, setLink] = useState<string | null>(null);
  const [active, setActive] = useState(coach?.active ?? true);
  const [eng, setEng] = useState(coach?.engagements_count ?? 0);

  if (!coach) {
    return <AppShell><div className="p-8">Coachen hittades inte. <Link to="/coacher" className="underline">Tillbaka</Link></div></AppShell>;
  }

  async function handleRequestUpdate() {
    try {
      const res = await requestUpdate({ data: { coachId: coach!.id } });
      setLink(`${window.location.origin}/coach-formular/${res.token}`);
      router.invalidate();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Kunde inte skapa länk.");
    }
  }

  async function handleSaveEngagements() {
    try {
      await updateEng({ data: { coachId: coach!.id, active, engagements_count: eng } });
      toast.success("Sparat");
      router.invalidate();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Kunde inte spara.");
    }
  }

  const initials = coach.full_name.split(/\s+/).slice(0, 2).map((s: string) => s[0]?.toUpperCase()).join("");

  return (
    <AppShell>
      <section className="px-8 pt-10 pb-16 max-w-5xl">
        <Link to="/coacher" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6"><ArrowLeft className="size-4" />Alla coacher</Link>

        <div className="flex items-start gap-6 mb-8">
          {coach.photo_url ? (
            <img src={coach.photo_url} alt={coach.full_name} className="size-24 rounded-2xl object-cover" />
          ) : (
            <div className="size-24 rounded-2xl bg-secondary flex items-center justify-center text-2xl font-medium">{initials || "?"}</div>
          )}
          <div className="flex-1">
            <h1 className="font-display text-3xl">{coach.full_name}</h1>
            <div className="text-sm text-muted-foreground mt-1">
              {coach.email}{coach.phone && ` · ${coach.phone}`}{coach.city && ` · ${coach.city}`}
            </div>
            <div className="flex gap-2 mt-3">
              {coach.active ? <Badge>Aktiv</Badge> : <Badge variant="secondary">Inaktiv</Badge>}
              {!coach.profile_completed_at && <Badge variant="outline">Profil ej ifylld</Badge>}
              {coach.linkedin_url && <a href={coach.linkedin_url} target="_blank" rel="noreferrer" className="text-xs underline text-muted-foreground">LinkedIn</a>}
            </div>
          </div>
          <Button variant="outline" onClick={handleRequestUpdate}><RefreshCcw className="size-4 mr-2" />Skapa länk för coach</Button>
        </div>

        {link && (
          <div className="rounded-2xl border border-border bg-card p-4 mb-6 space-y-3">
            <div>
              <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Länk till profilformulär</div>
              <div className="flex gap-2">
                <Input readOnly value={link} onFocus={(e) => e.currentTarget.select()} />
                <Button variant="secondary" onClick={() => { navigator.clipboard.writeText(link); toast.success("Kopierat"); }}>
                  <Copy className="size-4" />
                </Button>
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Länk till coach-portal (aktivera konto)</div>
              <div className="flex gap-2">
                <Input readOnly value={link.replace("/coach-formular/", "/coach-access/")} onFocus={(e) => e.currentTarget.select()} />
                <Button variant="secondary" onClick={() => { navigator.clipboard.writeText(link.replace("/coach-formular/", "/coach-access/")); toast.success("Kopierat"); }}>
                  <Copy className="size-4" />
                </Button>
              </div>
              <p className="text-[11px] text-muted-foreground mt-1">Skicka denna länk så coachen kan sätta lösenord och logga in i sin portal.</p>
            </div>
          </div>
        )}

        <div className="rounded-2xl border border-border bg-gradient-to-br from-card to-secondary/40 p-6 mb-6">
          <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-3">Översikt</div>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="md:col-span-2">
              <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5">Kort sammanfattning</div>
              <p className="text-sm leading-relaxed">
                {coach.bio
                  ? coach.bio.length > 220 ? coach.bio.slice(0, 220).trimEnd() + "…" : coach.bio
                  : <span className="text-muted-foreground italic">Ingen bio ifylld ännu.</span>}
              </p>
              {coach.previous_companies && (
                <div className="mt-4">
                  <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5">Tidigare bolag</div>
                  <p className="text-sm">{coach.previous_companies}</p>
                </div>
              )}
            </div>
            <div className="space-y-4">
              <Stat label="Engagemang" value={coach.engagements_count} />
              <Stat label="Branscher" value={coach.industries.length} />
              <Stat label="Expertområden" value={coach.functions.length} />
            </div>
          </div>
          {(coach.industries.length > 0 || coach.functions.length > 0) && (
            <div className="mt-5 pt-5 border-t border-border">
              <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Expertområden</div>
              <div className="flex flex-wrap gap-1.5">
                {[...coach.industries, ...coach.functions].map((t: string) => (
                  <span key={t} className="text-xs px-2.5 py-1 rounded-full bg-background border border-border">{t}</span>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="grid md:grid-cols-2 gap-4 mb-6">
          <Section title="Bio">{coach.bio || <Empty />}</Section>
          <Section title="Tidigare bolag">{coach.previous_companies || <Empty />}</Section>
          <TagSection title="Branscher" items={coach.industries} />
          <TagSection title="Funktionella områden" items={coach.functions} />
          <TagSection title="Affärsfaser" items={coach.business_phases} />
          <TagSection title="Språk" items={coach.languages} />
        </div>

        <div className="rounded-2xl border border-border bg-card p-5">
          <div className="text-xs uppercase tracking-wider text-muted-foreground mb-3">Engagemang & status</div>
          <div className="grid sm:grid-cols-3 gap-4 items-end">
            <div>
              <Label>Antal bolagsengagemang</Label>
              <Input type="number" min={0} value={eng} onChange={(e) => setEng(parseInt(e.target.value || "0", 10))} />
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={active} onCheckedChange={setActive} id="active" />
              <Label htmlFor="active">Aktiv coach</Label>
            </div>
            <Button onClick={handleSaveEngagements}>Spara</Button>
          </div>
        </div>
      </section>
    </AppShell>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5">
      <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">{title}</div>
      <div className="text-sm whitespace-pre-wrap">{children}</div>
    </div>
  );
}
function TagSection({ title, items }: { title: string; items: string[] }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5">
      <div className="text-xs uppercase tracking-wider text-muted-foreground mb-3">{title}</div>
      {items.length === 0 ? <Empty /> : (
        <div className="flex flex-wrap gap-1.5">
          {items.map((t) => <span key={t} className="text-xs px-2.5 py-1 rounded-full bg-secondary">{t}</span>)}
        </div>
      )}
    </div>
  );
}
function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-xl border border-border bg-background/60 p-3">
      <div className="text-2xl font-display">{value}</div>
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</div>
    </div>
  );
}
function Empty() { return <span className="text-muted-foreground italic">Inte angivet</span>; }
